import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Plus, Loader2, Copy, Pencil, Search, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeleteButton } from "@/components/DeleteButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function NewTemplateDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!name.trim()) { toast.error("Give the template a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/marketing/templates", { name: name.trim(), description: desc.trim() });
      navigate(`/marketing/templates/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-template-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Email Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monthly Product Update" data-testid="template-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Description</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this template for?" data-testid="template-desc-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="template-create-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create & Open Editor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EmailTemplates() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("templates-view") || "grid");
  const switchView = (v) => { setView(v); localStorage.setItem("templates-view", v); };

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["email-templates", search],
    queryFn: async () => (await api.get("/marketing/templates", { params: { search } })).data,
  });

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/marketing/templates/${id}/duplicate`);
      toast.success("Template duplicated");
      qc.invalidateQueries({ queryKey: ["email-templates"] });
      navigate(`/marketing/templates/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/marketing/templates/${id}`); toast.success("Template deleted"); qc.invalidateQueries({ queryKey: ["email-templates"] }); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };

  return (
    <div>
      <PageHeader title="Email Templates" subtitle="Design on-brand emails with the drag-and-drop builder. Templates stay editable.">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates…" className="h-9 pl-8 w-[220px]" data-testid="template-search" />
        </div>
        <div className="flex items-center rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => switchView("grid")} className={`h-9 px-2.5 flex items-center transition-colors ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`} title="Grid view" data-testid="templates-grid-view-btn"><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => switchView("list")} className={`h-9 px-2.5 flex items-center transition-colors ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`} title="List view" data-testid="templates-list-view-btn"><List className="h-4 w-4" /></button>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="new-template-btn"><Plus className="h-4 w-4" /> New Template</Button>
      </PageHeader>

      {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : templates.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">No templates yet — create your first on-brand email.</Card>
      ) : view === "list" ? (
        <Card className="divide-y divide-slate-100" data-testid="templates-list-view">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`template-row-${t.id}`}>
              <button className="flex items-center gap-3 min-w-0 flex-1 text-left" onClick={() => navigate(`/marketing/templates/${t.id}`)}>
                <div className="h-8 w-8 rounded-lg bg-[#FFEAE7] flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-[#f04935]" /></div>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm truncate">{t.name}</p>
                  <p className="text-xs text-slate-500 truncate">{t.description || "No description"}</p>
                </div>
              </button>
              <p className="text-[11px] text-slate-400 hidden md:block whitespace-nowrap">Edited by {t.updated_by_name || "—"} · {timeAgo(t.updated_at)}</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => navigate(`/marketing/templates/${t.id}`)} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => duplicate(t.id)} data-testid={`template-duplicate-${t.id}`}><Copy className="h-3.5 w-3.5" /> Duplicate</Button>
                <DeleteButton testId={`template-delete-${t.id}`} title="Delete this template?" description="This can't be undone. Campaigns already sent keep their snapshot." onConfirm={() => del(t.id)} />
              </div>
            </div>
          ))}
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid-view">
          {templates.map((t) => (
            <Card key={t.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-[#FFEAE7] flex items-center justify-center flex-shrink-0"><Mail className="h-4.5 w-4.5 text-[#f04935]" /></div>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-sm truncate">{t.name}</p>
                    <p className="text-xs text-slate-500 truncate">{t.description || "No description"}</p>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Edited by {t.updated_by_name || "—"} · {timeAgo(t.updated_at)}</p>
              <div className="flex items-center gap-1.5 mt-3">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs flex-1" onClick={() => navigate(`/marketing/templates/${t.id}`)} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => duplicate(t.id)} data-testid={`template-duplicate-${t.id}`}><Copy className="h-3.5 w-3.5" /> Duplicate</Button>
                <DeleteButton testId={`template-delete-${t.id}`} title="Delete this template?" description="This can't be undone. Campaigns already sent keep their snapshot." onConfirm={() => del(t.id)} />
              </div>
            </Card>
          ))}
        </div>
      )}
      <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
